import { Label } from "@radix-ui/react-label";
import { useState } from "react";
import { useController, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { AutoCreatedSecret } from "./auto-created-secret";
import { SecretInput } from "./secret-input";
import { StoredSecret } from "./stored-secret";
import { ConfigurationField } from "../../types";

export const STORED_SECRET_MASK = "••••••••";

type PasswordFieldProps = {
  field: Extract<ConfigurationField, { type: "password" }>;
  disabled?: boolean;
};

export function PasswordField({ field, disabled }: PasswordFieldProps) {
  const { control, formState } = useFormContext();
  const { field: input } = useController({ name: field.key, control });
  const { t } = useTranslation();
  const [isChanging, setIsChanging] = useState(false);

  const hasStoredSecret =
    field.encrypted === true && formState.defaultValues?.[field.key] === STORED_SECRET_MASK;
  const value = typeof input.value === "string" ? input.value : "";

  const placeholder = field.placeholder?.startsWith("integrations.")
    ? t(field.placeholder)
    : field.placeholder;

  const handleChange = (next: string) =>
    input.onChange(next === "" && hasStoredSecret ? STORED_SECRET_MASK : next);

  const undoChange = () => {
    input.onChange(STORED_SECRET_MASK);
    setIsChanging(false);
  };

  const renderControl = () => {
    if (!hasStoredSecret && field["auto-created"]) return <AutoCreatedSecret />;

    if (hasStoredSecret && !isChanging)
      return <StoredSecret fieldLabel={t(field.label)} disabled={disabled} onChange={() => setIsChanging(true)} />;

    return (
      <SecretInput
        ref={input.ref}
        id={field.key}
        name={input.name}
        onBlur={input.onBlur}
        autoFocus={isChanging}
        placeholder={hasStoredSecret ? t("integrations.modules.configure.fields.newSecretPlaceholder") : placeholder}
        disabled={disabled}
        value={value === STORED_SECRET_MASK ? "" : value}
        onChange={handleChange}
        onUndo={hasStoredSecret ? undoChange : undefined}
      />
    );
  };

  return (
    <div className="space-y-2">
      <Label htmlFor={field.key} className="text-base">
        {t(field.label)}
        {field.required && <span className="text-destructive ml-1">*</span>}
      </Label>
      {renderControl()}
    </div>
  );
}
